import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}

export default function Home() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [idade, setIdade] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [cidade, setCidade] = useState('');
  const [errors, setErrors] = useState({ nome: '', idade: '', whatsapp: '', cidade: '' });
  const [submitting, setSubmitting] = useState(false);

  const getTimeLeft = () => {
    const eventDate = new Date('2026-03-15T15:00:00-04:00');
    const now = new Date();
    const diff = eventDate.getTime() - now.getTime();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { days, hours, minutes, seconds };
  };

  const [timeLeft, setTimeLeft] = useState(getTimeLeft());

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, []);

  const scrollToForm = () => {
    document.getElementById('inscricao')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleVideoClick = (videoId: string) => {
    setActiveVideo(prev => prev === videoId ? null : videoId);
  };

  const formatWhatsapp = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const validate = (): boolean => {
    const e = { nome: '', idade: '', whatsapp: '', cidade: '' };
    if (!nome.trim()) e.nome = 'Informe o nome completo.';
    const idadeNum = parseInt(idade);
    if (!idade || isNaN(idadeNum) || idadeNum < 3 || idadeNum > 35) e.idade = 'A idade deve estar entre 3 e 35 anos.';
    const wRaw = whatsapp.replace(/\D/g, '');
    if (wRaw.length < 10 || wRaw.length > 11) e.whatsapp = 'Informe o WhatsApp com DDD. Ex: (66) 98129-9804';
    if (!cidade.trim()) e.cidade = 'Informe a cidade.';
    setErrors(e);
    return !e.nome && !e.idade && !e.whatsapp && !e.cidade;
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const wRaw = whatsapp.replace(/\D/g, '');
      const formData = new URLSearchParams();
      formData.set('nome', nome);
      formData.set('idade', idade);
      formData.set('whatsapp', wRaw);
      formData.set('cidade', cidade);

      const response = await fetch('https://readdy.ai/api/form/d6lbp4o5dsf7sr1n5sk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (!response.ok) {
        setSubmitting(false);
        alert('Erro ao enviar inscrição. Tente novamente.');
        return;
      }

      navigate('/obrigado');
    } catch {
      setSubmitting(false);
      alert('Erro ao enviar inscrição. Verifique sua conexão e tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black shadow-lg' : 'bg-black'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 lg:py-4 flex items-center justify-between">
          <img
            alt="Four Models"
            className="h-8 sm:h-10 lg:h-12 object-contain"
            src="https://static.readdy.ai/image/103ec5ada18b00c7d95516c6aa3b7243/3c6853a00769ef0b898cf7c7be2b9fe6.png"
          />
          <div className="hidden lg:flex items-center gap-8 text-gray-400 text-sm font-medium">
            <button onClick={() => document.getElementById('quem-pode')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-yellow-500 transition-colors cursor-pointer whitespace-nowrap">Quem Pode</button>
            <button onClick={() => document.getElementById('historias')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-yellow-500 transition-colors cursor-pointer whitespace-nowrap">Depoimentos</button>
            <button onClick={() => document.getElementById('participacao-especial')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-yellow-500 transition-colors cursor-pointer whitespace-nowrap">Convidado</button>
          </div>
          <button
            onClick={scrollToForm}
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-4 sm:px-7 lg:px-8 py-2 sm:py-2.5 lg:py-3 rounded-full font-black text-xs sm:text-sm lg:text-sm hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
          >
            INSCREVA-SE GRÁTIS
          </button>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              alt="Fashion Background"
              className="w-full h-full object-cover object-top"
              src="https://public.readdy.ai/ai/img_res/b1e6f203c1c86f7f2e02323a22506a53.jpg"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90"></div>
          </div>

          <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-24 pb-16 lg:pt-32 lg:pb-24">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-12 lg:gap-16">
              <div className="flex-1 text-center lg:text-left">
                <div className="flex flex-col sm:flex-row lg:flex-row items-center justify-center lg:justify-start gap-2 sm:gap-3 mb-6 lg:mb-8">
                  <span className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-4 py-1.5 rounded-full text-xs font-bold tracking-wider whitespace-nowrap flex items-center gap-1.5">
                    <i className="ri-calendar-event-fill text-sm"></i>
                    15/03 • 15h
                  </span>
                  <span className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-4 py-1.5 rounded-full text-xs font-bold tracking-wider whitespace-nowrap flex items-center gap-1.5">
                    <i className="ri-map-pin-fill text-yellow-500 text-sm"></i>
                    CAMPO NOVO DO PARECIS - MT
                  </span>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white mb-5 leading-tight">
                  Sua Carreira na<br />
                  Moda, TV e{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                    Redes Sociais
                  </span>
                  <br />Começa Aqui
                </h1>

                <p className="text-sm sm:text-base lg:text-lg text-gray-200 mb-2 font-light max-w-2xl mx-auto lg:mx-0">
                  Seletiva Oficial <strong className="text-white font-semibold">Four Models</strong> em{' '}
                  <strong className="text-yellow-500 font-bold">Campo Novo do Parecis - MT</strong>
                </p>

                <p className="text-base sm:text-lg lg:text-xl text-white mb-8 lg:mb-10 font-semibold max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  Descubra se você ou seu filho(a) tem perfil para moda, TV e internet.
                </p>

                <div className="flex flex-col sm:flex-row lg:flex-row items-center justify-center lg:justify-start gap-4">
                  <button
                    onClick={scrollToForm}
                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 sm:px-12 lg:px-14 py-4 lg:py-5 rounded-full text-base sm:text-lg lg:text-lg font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/40 hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    QUERO PARTICIPAR
                  </button>
                  <p className="text-yellow-500/80 text-xs font-medium whitespace-nowrap">
                    Inscrição gratuita • Sem experiência
                  </p>
                </div>

                <div className="mt-10 lg:mt-12 flex items-center justify-center lg:justify-start gap-6 sm:gap-10 text-white">
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-yellow-500">03–35</div>
                    <div className="text-xs text-gray-300 mt-1">Anos</div>
                  </div>
                  <div className="w-px h-10 bg-gray-600"></div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-yellow-500">100%</div>
                    <div className="text-xs text-gray-300 mt-1">Gratuito</div>
                  </div>
                  <div className="w-px h-10 bg-gray-600"></div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-yellow-500">0</div>
                    <div className="text-xs text-gray-300 mt-1">Exp. Necessária</div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:flex flex-col items-center justify-center w-[380px] xl:w-[420px] flex-shrink-0">
                <div className="bg-black/70 backdrop-blur-md border border-yellow-500/30 rounded-3xl px-8 py-8 w-full shadow-2xl shadow-black/60">
                  <div className="mb-5">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <span className="text-red-500 text-base animate-pulse">🔴</span>
                      <p className="text-white font-black text-base">
                        Vagas restantes: <span className="text-red-400 text-2xl">23</span>
                      </p>
                    </div>
                    <div className="flex justify-center gap-1 mb-2">
                      {Array.from({ length: 30 }).map((_, i) => (
                        <div key={i} className={`w-2 h-5 rounded-sm transition-all duration-700 ${i < 7 ? 'bg-green-500' : 'bg-white/10'}`} />
                      ))}
                    </div>
                    <p className="text-gray-400 text-xs text-center">
                      <span className="text-red-400 font-bold">23</span> de 30 vagas disponíveis
                    </p>
                  </div>

                  <div className="flex items-center gap-3 mb-4 max-w-xs mx-auto">
                    <div className="flex-1 h-px bg-yellow-500/20"></div>
                    <i className="ri-time-fill text-yellow-500/60 text-sm"></i>
                    <div className="flex-1 h-px bg-yellow-500/20"></div>
                  </div>

                  <div className="flex items-center gap-2 mb-3 justify-center">
                    <i className="ri-fire-fill text-red-400 text-base animate-pulse"></i>
                    <p className="text-white font-black text-sm text-center">
                      Apenas <span className="text-red-400 text-xl">23</span> vagas restantes!
                    </p>
                    <i className="ri-fire-fill text-red-400 text-base animate-pulse"></i>
                  </div>

                  <p className="text-gray-400 text-xs font-semibold tracking-widest uppercase text-center mb-4">
                    O evento começa em
                  </p>

                  <div className="flex items-center justify-center gap-2">
                    {[
                      { value: timeLeft.days, label: 'Dias' },
                      { value: timeLeft.hours, label: 'Horas' },
                      { value: timeLeft.minutes, label: 'Min' },
                      { value: timeLeft.seconds, label: 'Seg' },
                    ].map((item, idx) => (
                      <div key={item.label} className="flex items-center gap-2">
                        <div className="flex flex-col items-center">
                          <div className="bg-black/60 border border-yellow-500/30 rounded-xl w-16 h-16 flex items-center justify-center">
                            <span className="text-2xl font-black text-yellow-400 tabular-nums">
                              {String(item.value).padStart(2, '0')}
                            </span>
                          </div>
                          <span className="text-gray-500 text-[10px] font-semibold mt-1.5 tracking-wider uppercase">{item.label}</span>
                        </div>
                        {idx < 3 && <span className="text-yellow-500/60 text-xl font-black mb-4 select-none">:</span>}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={scrollToForm}
                    className="w-full mt-6 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black py-3.5 rounded-full text-sm font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    GARANTIR MINHA VAGA
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-2xl text-yellow-500"></i>
          </div>
        </section>

        {/* Countdown Section — mobile only */}
        <section className="py-8 sm:py-12 bg-gray-950 lg:hidden">
          <div className="max-w-lg mx-auto px-4">
            <div className="bg-black/80 border border-yellow-500/30 rounded-2xl px-4 sm:px-8 py-6 sm:py-8">
              <div className="mb-5">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="text-red-500 text-base animate-pulse">🔴</span>
                  <p className="text-white font-black text-sm sm:text-base">
                    Vagas restantes: <span className="text-red-400 text-lg sm:text-2xl">23</span>
                  </p>
                </div>
                <div className="flex justify-center gap-1 mb-2 overflow-hidden">
                  {Array.from({ length: 30 }).map((_, i) => (
                    <div key={i} className={`w-1.5 sm:w-2 h-4 sm:h-5 rounded-sm transition-all duration-700 ${i < 7 ? 'bg-green-500' : 'bg-white/10'}`} />
                  ))}
                </div>
                <p className="text-gray-400 text-xs text-center">
                  <span className="text-red-400 font-bold">23</span> de 30 vagas disponíveis
                </p>
              </div>

              <div className="flex items-center gap-3 mb-4 max-w-xs mx-auto">
                <div className="flex-1 h-px bg-yellow-500/20"></div>
                <i className="ri-time-fill text-yellow-500/60 text-sm"></i>
                <div className="flex-1 h-px bg-yellow-500/20"></div>
              </div>

              <div className="flex items-center gap-2 mb-3 justify-center">
                <i className="ri-fire-fill text-red-400 text-base animate-pulse"></i>
                <p className="text-white font-black text-sm sm:text-base text-center">
                  Restam apenas <span className="text-red-400 text-lg sm:text-xl">23</span> vagas para esta cidade.
                </p>
                <i className="ri-fire-fill text-red-400 text-base animate-pulse"></i>
              </div>

              <p className="text-gray-400 text-xs font-semibold tracking-widest uppercase text-center mb-4">
                O evento começa em
              </p>

              <div className="flex items-center justify-center gap-1.5 sm:gap-3">
                {[
                  { value: timeLeft.days, label: 'Dias' },
                  { value: timeLeft.hours, label: 'Horas' },
                  { value: timeLeft.minutes, label: 'Min' },
                  { value: timeLeft.seconds, label: 'Seg' },
                ].map((item, idx) => (
                  <div key={item.label} className="flex items-center gap-1.5 sm:gap-3">
                    <div className="flex flex-col items-center">
                      <div className="bg-black/60 border border-yellow-500/30 rounded-xl w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center">
                        <span className="text-xl sm:text-2xl font-black text-yellow-400 tabular-nums">
                          {String(item.value).padStart(2, '0')}
                        </span>
                      </div>
                      <span className="text-gray-500 text-[10px] font-semibold mt-1.5 tracking-wider uppercase">{item.label}</span>
                    </div>
                    {idx < 3 && <span className="text-yellow-500/60 text-xl font-black mb-4 select-none">:</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-10 sm:py-12 lg:py-16 bg-gradient-to-r from-yellow-500 to-yellow-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-40 h-40 bg-black rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-60 h-60 bg-black rounded-full translate-x-1/3 translate-y-1/3"></div>
          </div>
          <div className="max-w-5xl mx-auto px-5 lg:px-10 relative z-10">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-center lg:text-left">
                <p className="text-black font-black text-xl sm:text-2xl lg:text-3xl xl:text-4xl mb-2">
                  Não precisa experiência.
                </p>
                <p className="text-black/80 text-sm sm:text-base lg:text-lg font-medium">
                  Buscamos talentos naturais. Sua oportunidade começa em Campo Novo do Parecis - MT.
                </p>
              </div>
              <button
                onClick={scrollToForm}
                className="bg-black text-yellow-500 px-10 lg:px-14 py-4 lg:py-5 rounded-full text-sm lg:text-base font-bold hover:bg-gray-900 transition-all duration-300 shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto flex-shrink-0"
              >
                INSCREVA-SE AGORA
              </button>
            </div>
          </div>
        </section>

        {/* Who Can Participate */}
        <section id="quem-pode" className="py-14 sm:py-20 lg:py-28 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className="text-center mb-10 sm:mb-14 lg:mb-16">
              <p className="text-xs font-bold text-yellow-600 tracking-widest uppercase mb-3">Aberto para todos</p>
              <h4 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-3">
                <a href="#quem-pode">
                  Quem Pode{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">
                    Participar
                  </span>
                </a>
              </h4>
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-xl mx-auto">
                Aberto para todos os perfis. Sem experiência necessária.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5 lg:gap-6 mb-6 lg:mb-8">
              {[
                { icon: 'ri-bear-smile-line', title: 'Crianças', age: '03 a 12 anos', desc: 'Início promissor na carreira' },
                { icon: 'ri-user-smile-line', title: 'Adolescentes', age: '13 a 17 anos', desc: 'Fase ideal para começar' },
                { icon: 'ri-user-star-line', title: 'Jovens', age: '18 a 25 anos', desc: 'Auge para o mercado' },
                { icon: 'ri-user-heart-line', title: 'Adultos', age: '26 a 35 anos', desc: 'Maturidade e presença' },
                { icon: 'ri-camera-line', title: 'Influencers', age: 'Todas as idades', desc: 'Potencial digital' },
              ].map((item) => (
                <div key={item.title} className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-yellow-500 text-center group cursor-pointer">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-lg sm:text-2xl lg:text-3xl text-black`}></i>
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-black mb-0.5 lg:mb-1">{item.title}</h3>
                  <p className="text-xs sm:text-sm lg:text-sm text-yellow-600 font-semibold mb-1">{item.age}</p>
                  <p className="text-xs lg:text-sm text-gray-500 hidden sm:block">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-black rounded-2xl p-4 sm:p-7 lg:p-8 text-center">
              <p className="text-sm sm:text-xl lg:text-2xl text-white font-bold">
                <span className="text-yellow-500">Zero experiência?</span> Sem problema. Avaliamos seu potencial natural.
              </p>
            </div>
          </div>
        </section>

        {/* Why Participate */}
        <section className="py-14 sm:py-20 lg:py-28 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className="text-center mb-10 sm:mb-14 lg:mb-16">
              <p className="text-xs font-bold text-yellow-600 tracking-widest uppercase mb-3">Sua oportunidade real</p>
              <h4 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3">
                <a href="#beneficios">
                  Por Que{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">
                    Participar
                  </span>
                </a>
              </h4>
              <p className="text-sm sm:text-base lg:text-lg text-gray-400 max-w-xl mx-auto">
                Mais do que uma seletiva. Uma porta para o mercado.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-6">
              {[
                { icon: 'ri-user-search-line', title: 'Avaliação Profissional', desc: 'Feedback de especialistas sobre seu potencial real no mercado da moda e entretenimento.' },
                { icon: 'ri-star-line', title: 'Oportunidade Real', desc: 'Acesso direto a trabalhos em moda, publicidade e entretenimento nacional e internacional.' },
                { icon: 'ri-building-line', title: 'Ambiente Profissional', desc: 'Seletiva real com estrutura completa, equipe qualificada e metodologia comprovada.' },
                { icon: 'ri-rocket-line', title: 'Desenvolvimento Artístico', desc: 'Orientação profissional, networking e conexão com o mercado para impulsionar sua carreira.' },
              ].map((item) => (
                <div key={item.title} className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-5 sm:p-8 lg:p-8 border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 group cursor-pointer flex flex-col">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 mb-4 lg:mb-5">
                    <i className={`${item.icon} text-xl lg:text-2xl text-black`}></i>
                  </div>
                  <h3 className="text-base sm:text-xl lg:text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Event Information */}
        <section className="py-14 sm:py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className="text-center mb-10 sm:mb-14 lg:mb-16">
              <p className="text-xs font-bold text-yellow-600 tracking-widest uppercase mb-3">Detalhes do dia</p>
              <h4 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-3">
                <a href="#evento">
                  Informações do{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">
                    Evento
                  </span>
                </a>
              </h4>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 lg:gap-8">
              {[
                { icon: 'ri-calendar-event-fill', label: 'Data e Horário', main: '15/03/2026', sub: 'Domingo às 15h' },
                { icon: 'ri-map-pin-2-fill', label: 'Local', main: 'ACIC CDL', sub: 'Campo Novo do Parecis – MT' },
                { icon: 'ri-group-fill', label: 'Formato', main: 'Presencial', sub: 'Evento ao vivo' },
                { icon: 'ri-alarm-warning-fill', label: 'Vagas', main: 'Limitadas', sub: 'Por horário' },
              ].map((item) => (
                <div key={item.label} className="bg-white rounded-2xl p-4 sm:p-7 lg:p-10 shadow-md text-center border border-gray-100 hover:border-yellow-500 hover:shadow-xl transition-all duration-300 cursor-pointer group">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-lg sm:text-2xl lg:text-2xl text-black`}></i>
                  </div>
                  <p className="text-xs font-bold text-gray-500 mb-1 lg:mb-2 uppercase tracking-wider">{item.label}</p>
                  <p className="text-base sm:text-xl lg:text-2xl font-black text-black">{item.main}</p>
                  {item.sub && <p className="text-sm sm:text-base lg:text-base font-bold text-yellow-600 mt-1">{item.sub}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Authority Section */}
        <section className="py-14 sm:py-20 lg:py-28 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-3xl pointer-events-none"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
            <div className="text-center mb-10 sm:mb-14 lg:mb-16">
              <p className="text-xs font-bold text-yellow-600 tracking-widest uppercase mb-3">
                Tradição · Credibilidade · Resultados
              </p>
              <h4 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-3">
                <a href="#autoridade">
                  Quem Está Por Trás da{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">
                    Four Models?
                  </span>
                </a>
              </h4>
              <p className="text-sm sm:text-base lg:text-lg text-gray-500 max-w-2xl mx-auto">
                Mais de três décadas transformando vidas e lançando carreiras no mercado da moda e do entretenimento.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 lg:gap-6 mb-10 sm:mb-14 lg:mb-16">
              {[
                { icon: 'ri-trophy-fill', value: '38+', label: 'Anos de mercado' },
                { icon: 'ri-global-fill', value: '100+', label: 'Modelos no mercado internacional' },
                { icon: 'ri-star-fill', value: '1ª', label: 'Top model brasileira lançada' },
                { icon: 'ri-film-fill', value: 'TV & Moda', label: 'Atores e modelos nas maiores emissoras' },
              ].map((item) => (
                <div key={item.value} className="bg-black rounded-2xl p-4 sm:p-7 lg:p-8 text-center border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300 cursor-pointer group">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-base sm:text-xl lg:text-2xl text-black`}></i>
                  </div>
                  <p className="text-xl sm:text-3xl lg:text-4xl font-black text-yellow-500 mb-1">{item.value}</p>
                  <p className="text-gray-400 text-xs lg:text-sm">{item.label}</p>
                </div>
              ))}
            </div>

            <div className="bg-black rounded-2xl p-5 sm:p-10 lg:p-12 border border-yellow-500/20 mb-8 sm:mb-10 lg:mb-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 lg:gap-6">
                {[
                  { icon: 'ri-award-fill', text: 'Dirigida por Marla Drebes, referência nacional e internacional na descoberta de talentos.' },
                  { icon: 'ri-book-fill', text: 'Marla Drebes está no RankBrasil em 2004, é a Primeira brasileira a descobrir e projetar uma top model made in Brasil: Shirley Mallmann foi lançada em janeiro de 1995.' },
                  { icon: 'ri-user-star-fill', text: 'Responsável por lançar Shirley Mallmann — a primeira top model brasileira no cenário internacional.' },
                  { icon: 'ri-flashlight-fill', text: 'Revelou talentos como Jeisa Chiminazzo e diversos nomes que atuam no Brasil e no exterior.' },
                  { icon: 'ri-movie-2-fill', text: 'Trabalha também com atores, conectando talentos a testes nas maiores emissoras do país.' },
                  { icon: 'ri-medal-fill', text: 'Histórico de grandes projetos e recordes nacionais no segmento da moda.' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 sm:gap-4">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-sm sm:text-base text-black`}></i>
                    </div>
                    <p className="text-gray-300 text-sm lg:text-base leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={scrollToForm}
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 sm:px-10 lg:px-16 py-3.5 sm:py-4 lg:py-5 rounded-full text-sm lg:text-base font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
              >
                GARANTIR MINHA VAGA
              </button>
            </div>
          </div>
        </section>

        {/* Success Stories Section */}
        <section id="historias" className="py-14 sm:py-20 lg:py-28 bg-black relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              alt="Background"
              className="w-full h-full object-cover object-top"
              src="https://barradobugres.fourmodelsseletivas.com.br/static/media/bg-testimonials.jpg"
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
            <div className="text-center mb-10 sm:mb-14 lg:mb-16">
              <p className="text-xs font-bold text-yellow-600 tracking-widest uppercase mb-3">Resultados reais</p>
              <h4 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3">
                <a href="#historias">
                  Histórias de{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">
                    Sucesso
                  </span>
                </a>
              </h4>
              <p className="text-sm sm:text-base lg:text-lg text-gray-400 max-w-xl mx-auto">
                Assista os depoimentos de quem já passou pelas nossas seletivas.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 mb-8 sm:mb-10 lg:mb-12">
              {[
                { id: 'fWZeK74x6Us', name: 'Taís Pinheiro', location: 'Sinop - MT', role: 'Modelo Internacional' },
                { id: 'GEI7zFUsukk', name: 'Beatriz Zachert', location: 'Sorriso - MT', role: 'Modelo Internacional' },
                { id: 'Xvk5lA_x5ic', name: 'Douglas Malmman', location: 'CEO da Mädchen Models', role: 'Modelo Internacional' },
                { id: 'zLSADgvoVXI', name: 'Valentina Marques', location: 'Presidente Figueiredo - AM', role: 'Atriz' },
              ].map((video) => (
                <div key={video.id} className="bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-yellow-500/20 hover:border-yellow-500/50 transition-all duration-300 flex flex-col">
                  <div className="relative w-full aspect-video bg-black">
                    {activeVideo === video.id ? (
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${video.id}?autoplay=1`}
                        title={`Depoimento de ${video.name}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div
                        onClick={() => handleVideoClick(video.id)}
                        className="absolute inset-0 w-full h-full cursor-pointer group"
                      >
                        <img
                          alt={`Depoimento de ${video.name}`}
                          className="w-full h-full object-cover object-top"
                          src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                          <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-yellow-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-yellow-500/30">
                            <i className="ri-play-fill text-black text-2xl lg:text-3xl ml-1"></i>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-4 sm:p-5 lg:p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-0.5 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-sm text-yellow-500"></i>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-auto">
                      <div className="w-9 h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-black font-bold text-sm">{video.name[0]}</span>
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm lg:text-base">{video.name}</p>
                        <p className="text-gray-500 text-xs">{video.location} • {video.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 lg:gap-8">
              {[
                { value: '+50.000', label: 'Participantes em seletivas' },
                { value: '+1.000', label: 'Talentos descobertos' },
                { value: 'Brasil', label: 'Inteiro atendido' },
              ].map((stat) => (
                <div key={stat.value} className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5 lg:p-8 text-center hover:bg-yellow-500/15 transition-all duration-300">
                  <p className="text-2xl sm:text-3xl lg:text-5xl font-black text-yellow-500 mb-1 lg:mb-2">{stat.value}</p>
                  <p className="text-gray-400 text-xs sm:text-sm lg:text-base">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Special Guest Section */}
        <section id="participacao-especial" className="py-16 sm:py-28 lg:py-32 relative overflow-hidden bg-gradient-to-br from-gray-950 via-[#0a0a1a] to-gray-950">
          <div className="absolute top-0 left-0 w-72 h-72 lg:w-[500px] lg:h-[500px] bg-yellow-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 lg:w-[600px] lg:h-[600px] bg-yellow-500/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
            <div className="text-center mb-10 sm:mb-16 lg:mb-20">
              <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-4 py-2 mb-5">
                <i className="ri-star-fill text-yellow-500 text-xs"></i>
                <span className="text-yellow-500 text-xs font-semibold tracking-widest uppercase">
                  Participação Especial Confirmada
                </span>
                <i className="ri-star-fill text-yellow-500 text-xs"></i>
              </div>
              <h4 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white">
                <a href="#participacao-especial">
                  Etapa{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                    Final
                  </span>
                </a>
              </h4>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-4 lg:-inset-8 bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-3xl blur-2xl"></div>
                  <div className="relative w-[260px] h-[340px] sm:w-[340px] sm:h-[440px] lg:w-[420px] lg:h-[540px] rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-yellow-500/20">
                    <img
                      alt="Léo Belmonte - Ator brasileiro conhecido por Carrossel SBT"
                      title="Léo Belmonte ator participação especial Four Models"
                      className="w-full h-full object-cover object-top"
                      src="https://static.readdy.ai/image/103ec5ada18b00c7d95516c6aa3b7243/bcdca41a21e27164614ffcdeaee4e2ad.jpeg"
                    />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent h-32"></div>
                  </div>
                  <div className="absolute -top-3 -right-3 w-10 h-10 lg:w-14 lg:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/30 rotate-6">
                    <i className="ri-clapperboard-fill text-black text-lg lg:text-2xl"></i>
                  </div>
                </div>
              </div>

              <div className="text-center lg:text-left">
                <div className="flex items-center gap-2 justify-center lg:justify-start mb-2">
                  <div className="w-8 lg:w-12 h-[2px] bg-yellow-500"></div>
                  <span className="text-yellow-500 text-xs font-semibold tracking-widest uppercase">Convidado Especial</span>
                </div>

                <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-2">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600">
                    Léo Belmonte
                  </span>
                </h3>

                <p className="text-gray-400 text-sm lg:text-base font-medium mb-6 lg:mb-8 flex items-center justify-center lg:justify-start gap-2">
                  <i className="ri-film-fill text-yellow-500/70 text-base"></i>
                  Ator • Carrossel (SBT) • 3% (Netflix)
                </p>

                <div className="space-y-4 lg:space-y-5 mb-8 lg:mb-10">
                  <p className="text-gray-300 text-sm lg:text-base leading-relaxed">
                    Na etapa final da <strong className="text-white">Convenção Four Models</strong>, contaremos com a participação especial do ator <strong className="text-white">Léo Belmonte</strong>, conhecido por interpretar <strong className="text-white">Jorge na novela Carrossel (SBT)</strong>, além de participação na série <strong className="text-white">3%</strong> da Netflix.
                  </p>
                  <p className="text-gray-300 text-sm lg:text-base leading-relaxed">
                    Sua presença reforça o compromisso da <strong className="text-white">Four Models</strong> em proporcionar <strong className="text-yellow-500">experiência real de mercado</strong> e conexão com profissionais atuantes.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3 lg:gap-4 mb-8 lg:mb-10">
                  {[
                    { icon: 'ri-tv-fill', title: 'Carrossel', sub: 'SBT' },
                    { icon: 'ri-netflix-fill', title: '3%', sub: 'Netflix' },
                    { icon: 'ri-movie-2-fill', title: 'Audiovisual', sub: 'Brasileiro' },
                  ].map((item) => (
                    <div key={item.title} className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4 lg:p-5 text-center hover:border-yellow-500/30 transition-all duration-300">
                      <div className="w-8 h-8 lg:w-10 lg:h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <i className={`${item.icon} text-yellow-500 text-sm lg:text-base`}></i>
                      </div>
                      <p className="text-white text-xs lg:text-sm font-semibold">{item.title}</p>
                      <p className="text-gray-500 text-[11px] lg:text-xs">{item.sub}</p>
                    </div>
                  ))}
                </div>

                <button
                  onClick={scrollToForm}
                  className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-10 lg:px-14 py-4 lg:py-5 rounded-full text-sm lg:text-base font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
                >
                  QUERO PARTICIPAR
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Registration Form */}
        <section id="inscricao" className="py-14 sm:py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className="text-center mb-8 sm:mb-10 lg:mb-14">
              <p className="text-xs font-bold text-yellow-600 tracking-widest uppercase mb-3">Inscrição gratuita</p>
              <h4 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-3">
                <a href="#inscricao">
                  Garanta sua{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">
                    vaga gratuita
                  </span>
                </a>
              </h4>
              <p className="text-sm lg:text-base text-gray-500">
                Leva menos de 30 segundos para se inscrever.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start justify-center">
              {/* Info panel — desktop only */}
              <div className="hidden lg:flex flex-col gap-6 w-[380px] xl:w-[420px] flex-shrink-0 pt-2">
                <div className="bg-black rounded-2xl p-8 border border-yellow-500/20">
                  <h3 className="text-white font-black text-xl mb-6">Detalhes do Evento</h3>
                  <div className="space-y-5">
                    {[
                      { icon: 'ri-calendar-event-fill', label: 'Data', value: '15 de Março de 2026' },
                      { icon: 'ri-time-fill', label: 'Horário', value: '15h (Domingo)' },
                      { icon: 'ri-map-pin-2-fill', label: 'Local', value: 'ACIC CDL — Campo Novo do Parecis - MT' },
                      { icon: 'ri-money-dollar-circle-fill', label: 'Investimento', value: '100% Gratuito' },
                      { icon: 'ri-group-fill', label: 'Faixa etária', value: '03 a 35 anos' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-4">
                        <div className="w-9 h-9 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-lg sm:text-2xl lg:text-2xl text-black`}></i>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wider font-semibold">{item.label}</p>
                          <p className="text-white text-sm font-semibold mt-0.5">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <i className="ri-shield-check-fill text-yellow-500 text-xl"></i>
                    <p className="text-black font-bold text-sm">Inscrição 100% segura</p>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    Seus dados são utilizados apenas para confirmação da sua vaga. Não compartilhamos com terceiros.
                  </p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-fire-fill text-red-500 text-base animate-pulse"></i>
                    <p className="text-red-700 font-black text-sm">Apenas 23 vagas restantes!</p>
                  </div>
                  <p className="text-red-600 text-xs leading-relaxed">
                    As vagas são limitadas por horário. Garanta a sua antes que esgotem.
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="w-full lg:max-w-xl">
                <form
                  id="form-inscricao"
                  data-readdy-form
                  onSubmit={handleFormSubmit}
                  className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 border-2 border-gray-100"
                >
                  <div className="space-y-5">
                    {/* Nome */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Nome Completo <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="nome"
                        required
                        value={nome}
                        onChange={(e) => { setNome(e.target.value); setErrors(prev => ({ ...prev, nome: '' })); }}
                        className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 placeholder-gray-400 ${errors.nome ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'}`}
                        placeholder="Nome completo do participante"
                      />
                      {errors.nome && (
                        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                          <i className="ri-error-warning-fill text-sm"></i>
                          {errors.nome}
                        </p>
                      )}
                    </div>

                    {/* Idade */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Idade <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="idade"
                        required
                        type="text"
                        inputMode="numeric"
                        value={idade}
                        onKeyDown={(e) => {
                          const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                          if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setIdade(val);
                          setErrors(prev => ({ ...prev, idade: '' }));
                        }}
                        className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 placeholder-gray-400 ${errors.idade ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'}`}
                        placeholder="Idade (entre 3 e 35 anos)"
                      />
                      {errors.idade && (
                        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                          <i className="ri-error-warning-fill text-sm"></i>
                          {errors.idade}
                        </p>
                      )}
                      <p className="text-gray-400 text-xs mt-1.5">Apenas números entre 3 e 35 anos</p>
                    </div>

                    {/* WhatsApp */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        WhatsApp <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="whatsapp"
                        required
                        type="tel"
                        inputMode="numeric"
                        value={whatsapp}
                        onKeyDown={(e) => {
                          const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                          if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e) => {
                          const formatted = formatWhatsapp(e.target.value);
                          setWhatsapp(formatted);
                          setErrors(prev => ({ ...prev, whatsapp: '' }));
                        }}
                        className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 placeholder-gray-400 ${errors.whatsapp ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'}`}
                        placeholder="(66) 98129-9804"
                      />
                      {errors.whatsapp && (
                        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                          <i className="ri-error-warning-fill text-sm"></i>
                          {errors.whatsapp}
                        </p>
                      )}
                      <p className="text-gray-400 text-xs mt-1.5">Digite o DDD + número. Ex: (66) 98129-9804</p>
                    </div>

                    {/* Cidade */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Cidade <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="cidade"
                        required
                        type="text"
                        value={cidade}
                        onChange={(e) => { setCidade(e.target.value); setErrors(prev => ({ ...prev, cidade: '' })); }}
                        className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none transition-colors text-sm text-gray-900 placeholder-gray-400 ${errors.cidade ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-yellow-500'}`}
                        placeholder="Ex: Campo Novo do Parecis - MT"
                      />
                      {errors.cidade && (
                        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                          <i className="ri-error-warning-fill text-sm"></i>
                          {errors.cidade}
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-7 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-6 py-4 lg:py-5 rounded-full text-sm lg:text-base font-black hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer tracking-wide flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        Enviando...
                      </>
                    ) : (
                      'GARANTIR MINHA VAGA GRATUITA'
                    )}
                  </button>

                  <p className="text-center text-xs text-gray-400 mt-4">
                    Inscrição 100% gratuita • Seus dados estão seguros
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black py-10 sm:py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-8 lg:gap-16 mb-8 lg:mb-12">
            <div className="flex flex-col items-center lg:items-start gap-4">
              <img
                alt="Four Models"
                className="h-14 sm:h-20 lg:h-24 object-contain"
                src="https://static.readdy.ai/image/103ec5ada18b00c7d95516c6aa3b7243/f6ba88513f846dcaf1ba402d9a75d603.png"
              />
              <p className="text-gray-500 text-xs lg:text-sm max-w-xs text-center lg:text-left leading-relaxed">
                Há mais de 38 anos descobrindo e lançando talentos no mercado da moda, TV e entretenimento.
              </p>
            </div>

            <div className="text-center lg:text-left">
              <p className="text-yellow-500 font-bold text-xs uppercase tracking-widest mb-3">
                Evento
              </p>
              <p className="text-white font-bold text-base mb-1">
                Seletiva Presencial Four Models
              </p>
              <p className="text-gray-400 text-sm">15 de Março de 2026 às 15h</p>
              <p className="text-gray-400 text-sm">ACIC CDL</p>
              <p className="text-gray-400 text-sm">Campo Novo do Parecis – MT</p>
            </div>

            <div className="text-center lg:text-left">
              <p className="text-yellow-500 font-bold text-xs uppercase tracking-widest mb-3">
                Redes Sociais
              </p>
              <div className="flex items-center gap-4 justify-center lg:justify-start">
                <a
                  href="https://www.instagram.com/fourmodelsoficial/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:border-yellow-500 hover:text-yellow-500 text-gray-400 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a
                  href="https://www.facebook.com/fourmodelsoficial"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:border-yellow-500 hover:text-yellow-500 text-gray-400 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-facebook-circle-line text-xl"></i>
                </a>
                <a
                  href="https://wa.me/?text=Tenho%20d%C3%BAvidas%20sobre%20a%20sele%C3%A7%C3%B5e%20de%20Campo%20Novo%20do%20Parecis%20-%20MT%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es!"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:border-yellow-500 hover:text-yellow-500 text-gray-400 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-5 text-center">
            <p className="text-gray-500 text-xs">
              © 2026 Four Models. Todos os direitos reservados. •{' '}
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow noopener noreferrer" className="hover:text-yellow-500 transition-colors font-semibold cursor-pointer">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
